import { Badge, Box, Button, Flex, Separator, Text } from '@chakra-ui/react';
import SupplyCommitmentPopover from '@features/supply/SupplyCommitmentPopover';
import { allCommodities, Contract } from 'helpers/fakeData';
import { InventoryNeed } from '@features/supply/fakeSupplyChainData';
import { formatDate } from 'helpers/dates';
import { memo, useMemo } from 'react';
import { LuThumbsUp } from 'react-icons/lu';

interface Props {
  contract: Contract;
  hideSupplyButton?: boolean;
}

const formatCurrency = (amount: number) => {
  return `GHS ${amount.toLocaleString()}`;
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Text
    fontSize="xs"
    fontWeight="semibold"
    textTransform="uppercase"
    letterSpacing="wide"
    color="fg.subtle"
  >
    {children}
  </Text>
);

const Highlight = ({ children }: { children: React.ReactNode }) => (
  <Text as="span" fontWeight="semibold" color="fg">
    {children}
  </Text>
);

const ContractDetailView = ({ contract, hideSupplyButton }: Props) => {
  const commodity = useMemo(
    () => allCommodities.find((c) => c.id === contract.commodityId),
    [contract.commodityId],
  );

  const commodityName = commodity?.name ?? 'Unknown Commodity';

  const contractNeeds: InventoryNeed[] = useMemo(
    () => [
      {
        id: contract.id,
        itemName: commodityName,
        quantity: contract.contractSize,
        unit: 'units',
        deliveryBy: contract.settlementDate,
      },
    ],
    [contract.id, commodityName, contract.contractSize, contract.settlementDate],
  );
  const hedgeRange = formatCurrency(contract.forwardPrice * (contract.hedgePct / 100));
  const exampleSupply = Math.round(contract.contractSize / 2);
  const exampleDeposit = Math.round(exampleSupply * (contract.hedgePct / 100));

  return (
    <Flex direction="column" gap={5} w="full" minW={0} py={4} px={{ base: 2, sm: 4 }}>
      <Box>
        <SectionLabel>Overview</SectionLabel>
        <Text mt={2} fontSize="md" lineHeight="tall" color="fg.muted">
          <Highlight>{contract.buyerName}</Highlight> wants to buy{' '}
          <Highlight>{formatCurrency(contract.contractSize)}</Highlight> of{' '}
          <Highlight>{commodityName}</Highlight> at{' '}
          <Highlight>{formatCurrency(contract.forwardPrice)}</Highlight> per unit, with delivery by{' '}
          <Highlight>{formatDate(contract.settlementDate)}</Highlight>.
        </Text>
      </Box>

      <Separator borderColor="border.light" />

      {contract.contractType === 'firm' && (
        <>
          <Box>
            <SectionLabel>Price Protection</SectionLabel>
            <Text mt={2} fontSize="md" lineHeight="tall" color="fg.muted">
              Both sides are protected against price swings of up to{' '}
              <Highlight>{contract.hedgePct}%</Highlight> (up to{' '}
              <Highlight>+/- {hedgeRange}</Highlight>). Any movement beyond that is not covered.
            </Text>
          </Box>

          <Separator borderColor="border.light" />

          <Box>
            <SectionLabel>How to Participate</SectionLabel>
            <Text mt={2} fontSize="md" lineHeight="tall" color="fg.muted">
              To supply, you deposit <Highlight>{contract.hedgePct}%</Highlight> of the value you
              want to fill. For example, supplying{' '}
              <Highlight>{formatCurrency(exampleSupply)}</Highlight> requires a{' '}
              <Highlight>{formatCurrency(exampleDeposit)}</Highlight> deposit.
            </Text>
          </Box>
        </>
      )}

      {contract.contractType === 'soft' && (
        <Box>
          <SectionLabel>What is a Soft Contract?</SectionLabel>
          <Text mt={2} fontSize="md" lineHeight="tall" color="fg.muted">
            This is a non-binding request — the buyer hasn{"'"}t put up any collateral yet. Upvote
            it to let the buyer know you{"'"}re interested in supplying. Once enough sellers show
            interest, the buyer can turn this into a firm contract with locked-in terms.
          </Text>
        </Box>
      )}

      <Separator borderColor="border.light" />

      <Flex gap={4} flexWrap="wrap" alignItems="flex-end" justifyContent="space-between">
        <Flex gap={4} flexWrap="wrap">
          <Flex direction="column" gap={1}>
            <Text fontSize="xs" color="fg.subtle">
              Status
            </Text>
            <Badge
              size="sm"
              colorPalette={
                contract.status === 'ACTIVE'
                  ? 'green'
                  : contract.status === 'PENDING'
                    ? 'yellow'
                    : 'gray'
              }
            >
              {contract.status}
            </Badge>
          </Flex>
          <Flex direction="column" gap={1}>
            <Text fontSize="xs" color="fg.subtle">
              Filled
            </Text>
            <Text fontSize="sm" fontWeight="medium">
              {contract.fillPercentage}%
            </Text>
          </Flex>
          <Flex direction="column" gap={1}>
            <Text fontSize="xs" color="fg.subtle">
              Max. Deposit
            </Text>
            <Text fontSize="sm" fontWeight="medium">
              {formatCurrency(Math.round(contract.contractSize * (contract.hedgePct / 100)))}
            </Text>
          </Flex>
          <Flex direction="column" gap={1}>
            <Text fontSize="xs" color="fg.subtle">
              Created by
            </Text>
            <Text fontSize="sm" fontWeight="medium">
              {contract.createdBy}
            </Text>
          </Flex>
        </Flex>
        {!hideSupplyButton && contract.contractType === 'firm' && (
          <SupplyCommitmentPopover needs={contractNeeds} />
        )}
        {!hideSupplyButton && contract.contractType === 'soft' && (
          <Button size="sm" borderRadius="lg" variant="surfaceOutline" aria-label="Upvote contract">
            <LuThumbsUp />
            Interested
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export default memo(ContractDetailView);
